import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

export default class extends Controller {
  static targets = ["chart"]
  static values = {
    type: String,
    data: Array,
    options: Object,
    labels: Array,
    colors: Array,
    metrics: Array
  }

  connect() {
    // Initialize retry counter
    this.retryCount = 0
    this.maxRetries = 50 // Maximum 50 retries = 5 seconds
    
    // Ensure DOM is ready and container is properly sized
    this.initializeChart()
    
    // Add resize observer to handle window resizing
    this.resizeObserver = new ResizeObserver(() => {
      this.handleResize()
    })
    this.resizeObserver.observe(this.element)
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    // Reset retry counter on disconnect
    this.retryCount = 0
  }

  initializeChart() {
    if (!this.element || !this.typeValue) return

    // Ensure container has dimensions
    const rect = this.element.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) {
      // Check if we've exceeded maximum retries
      if (this.retryCount >= this.maxRetries) {
        console.warn('Chart initialization failed: Container dimensions remain zero after maximum retries')
        this.showFallbackContent()
        return
      }
      
      // Increment retry counter and retry after a short delay
      this.retryCount++
      setTimeout(() => this.initializeChart(), 100)
      return
    }

    // Reset retry counter on successful initialization
    this.retryCount = 0

    const chartType = this.typeValue
    let options = {}

    try {
      switch (chartType) {
        case 'line':
          options = this.buildLineChartOptions()
          break
        case 'bar':
          options = this.buildBarChartOptions()
          break
        case 'pie':
          options = this.buildPieChartOptions()
          break
        case 'area':
          options = this.buildAreaChartOptions()
          break
        default:
          options = this.buildDefaultOptions()
      }

      // Merge with any additional options
      if (this.hasOptionsValue) {
        options = { ...options, ...this.optionsValue }
      }

      // Add explicit dimensions
      options.chart = {
        ...options.chart,
        width: '100%',
        height: options.chart.height || this.getChartHeight()
      }

      this.chart = new ApexCharts(this.element, options)
      
      this.chart.render().then(() => {
        // Hide placeholder content after successful render
        this.hidePlaceholder()
        
        // Force a resize to ensure proper positioning
        setTimeout(() => {
          if (this.chart) {
            this.chart.windowResizeHandler()
          }
        }, 200)
      }).catch(error => {
        console.error('Error rendering chart:', error)
        this.showFallbackContent()
      })
    } catch (error) {
      console.error('Error initializing chart:', error)
      this.showFallbackContent()
    }
  }

  getChartHeight() {
    // Get height from container or default based on chart type
    const containerHeight = this.element.offsetHeight
    if (containerHeight > 0) {
      return containerHeight
    }
    
    // Default heights based on chart type
    switch (this.typeValue) {
      case 'line':
      case 'area':
        return 250
      case 'bar':
      case 'pie':
        return 120
      default:
        return 200
    }
  }

  buildLineChartOptions() {
    const data = this.hasDataValue ? this.dataValue : []
    const metrics = this.hasMetricsValue ? this.metricsValue : ['completion_rate', 'velocity']
    const colors = this.hasColorsValue ? this.colorsValue : ['#3b82f6', '#10b981']

    // Process data for line chart
    const series = this.processTimeSeriesData(data, metrics)

    return {
      chart: {
        type: 'line',
        height: 250,
        toolbar: { show: false },
        animations: { enabled: true },
        redrawOnParentResize: true,
        redrawOnWindowResize: true
      },
      series: series,
      xaxis: {
        type: 'datetime',
        labels: {
          datetimeUTC: false,
          format: 'MMM dd'
        }
      },
      yaxis: [
        {
          title: { text: 'Completion Rate (%)' },
          min: 0,
          max: 100
        },
        {
          opposite: true,
          title: { text: 'Velocity (tasks/day)' },
          min: 0
        }
      ],
      colors: colors,
      stroke: { curve: 'smooth', width: 3 },
      markers: { size: 4 },
      tooltip: {
        shared: true,
        intersect: false,
        x: { format: 'MMM dd, yyyy' }
      },
      legend: { position: 'top', horizontalAlign: 'right' },
      responsive: [{
        breakpoint: 768,
        options: {
          chart: {
            height: 200
          }
        }
      }]
    }
  }

  buildBarChartOptions() {
    const data = this.hasDataValue ? this.dataValue : []
    const labels = this.hasLabelsValue ? this.labelsValue : []
    const colors = this.hasColorsValue ? this.colorsValue : ['#9ca3af', '#3b82f6', '#eab308', '#22c55e']

    return {
      chart: {
        type: 'bar',
        height: 120,
        toolbar: { show: false },
        redrawOnParentResize: true,
        redrawOnWindowResize: true
      },
      series: [{
        name: 'Tasks',
        data: data
      }],
      xaxis: {
        categories: labels,
        labels: { style: { fontSize: '11px' } }
      },
      yaxis: {
        labels: { style: { fontSize: '11px' } }
      },
      colors: colors,
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '60%',
          borderRadius: 4
        }
      },
      dataLabels: { enabled: false },
      tooltip: {
        y: { formatter: (val) => `${val} tasks` }
      },
      responsive: [{
        breakpoint: 480,
        options: {
          chart: {
            height: 100
          }
        }
      }]
    }
  }

  buildPieChartOptions() {
    const data = this.hasDataValue ? this.dataValue : []
    const labels = this.hasLabelsValue ? this.labelsValue : []
    const colors = this.hasColorsValue ? this.colorsValue : ['#ef4444', '#f97316', '#eab308', '#22c55e']

    // Filter out zero values for cleaner pie chart
    const filteredData = []
    const filteredLabels = []
    const filteredColors = []

    data.forEach((value, index) => {
      if (value > 0) {
        filteredData.push(value)
        filteredLabels.push(labels[index] || `Item ${index + 1}`)
        filteredColors.push(colors[index] || colors[index % colors.length])
      }
    })

    return {
      chart: {
        type: 'pie',
        height: 120,
        redrawOnParentResize: true,
        redrawOnWindowResize: true
      },
      series: filteredData,
      labels: filteredLabels,
      colors: filteredColors,
      legend: { show: false },
      dataLabels: { enabled: false },
      plotOptions: {
        pie: {
          size: 80,
          donut: {
            size: '0%'
          }
        }
      },
      tooltip: {
        y: { formatter: (val) => `${val} tasks` }
      },
      responsive: [{
        breakpoint: 480,
        options: {
          chart: {
            height: 100
          },
          plotOptions: {
            pie: {
              size: 60
            }
          }
        }
      }]
    }
  }

  buildAreaChartOptions() {
    const data = this.hasDataValue ? this.dataValue : []
    const colors = this.hasColorsValue ? this.colorsValue : ['#3b82f6']

    return {
      chart: {
        type: 'area',
        height: 250,
        toolbar: { show: false },
        redrawOnParentResize: true,
        redrawOnWindowResize: true
      },
      series: data,
      colors: colors,
      stroke: { curve: 'smooth', width: 2 },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.3
        }
      },
      tooltip: { shared: true, intersect: false },
      responsive: [{
        breakpoint: 768,
        options: {
          chart: {
            height: 200
          }
        }
      }]
    }
  }

  buildDefaultOptions() {
    return {
      chart: {
        type: this.typeValue || 'line',
        height: 250,
        toolbar: { show: false },
        redrawOnParentResize: true,
        redrawOnWindowResize: true
      },
      series: this.hasDataValue ? this.dataValue : [],
      colors: this.hasColorsValue ? this.colorsValue : ['#3b82f6']
    }
  }

  processTimeSeriesData(data, metrics) {
    if (!Array.isArray(data) || data.length === 0) {
      return []
    }

    const series = []

    metrics.forEach((metric, index) => {
      const seriesData = data.map(item => ({
        x: new Date(item.date).getTime(),
        y: item[metric] || 0
      }))

      series.push({
        name: this.formatMetricName(metric),
        data: seriesData,
        yAxisIndex: metric === 'completion_rate' ? 0 : 1
      })
    })

    return series
  }

  formatMetricName(metric) {
    const names = {
      'completion_rate': 'Completion Rate',
      'velocity': 'Velocity',
      'created': 'Created',
      'completed': 'Completed'
    }
    return names[metric] || metric.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  hidePlaceholder() {
    const placeholder = this.element.querySelector('.border-dashed')
    if (placeholder) {
      placeholder.style.display = 'none'
    }
  }

  showFallbackContent() {
    // If chart fails to load, show the placeholder content
    const placeholder = this.element.querySelector('.border-dashed')
    if (placeholder) {
      placeholder.style.display = 'flex'
    }
  }

  handleResize() {
    if (this.chart) {
      // Debounce resize events
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout)
      }
      
      this.resizeTimeout = setTimeout(() => {
        this.chart.windowResizeHandler()
      }, 250)
    }
  }

  // Update chart data dynamically
  updateChart(newData) {
    if (this.chart && newData) {
      this.chart.updateSeries(newData)
    }
  }

  // Refresh chart with new options
  refreshChart() {
    if (this.chart) {
      this.chart.destroy()
    }
    this.initializeChart()
  }

  // Helper method for real-time updates
  updateData(data) {
    if (this.chart) {
      this.chart.updateSeries(data, true)
    }
  }
}

